import type { Maybe } from 'core/maybe';
import { Just, Nothing } from 'core/maybe';
import { unreachable } from 'utils/unreachable';

export type Empty = ['empty'];
export const Empty = (): Empty => ['empty'];

export type Loading = ['loading'];
export const Loading = (): Loading => ['loading'];

export type Loaded<T> = ['loaded', T];
export const Loaded = <T>(result: T): Loaded<T> => ['loaded', result];

export type Refreshing<T> = ['refreshing', T];
export const Refreshing = <T>([, current]: Loaded<T>): Refreshing<T> => [
  'refreshing',
  current,
];

export type Creating<T> = ['creating', T];
export const Creating = <T>(value: T): Creating<T> => ['creating', value];

export type Content<T> = Empty | Loading | Loaded<T>;

export type Updating<T> = ['updating', T, T];
export const Updating = <T>(next: T, previous: T): Updating<T> => [
  'updating',
  next,
  previous,
];

export type Deleting<T> = ['deleting', T];
export const Deleting = <T>(previous: T): Deleting<T> => ['deleting', previous];

export const Content = {
  isEmpty: <T>(value: Content<T>): value is Empty => value[0] === 'empty',
  isLoading: <T>(value: Content<T>): value is Loading => value[0] === 'loading',
  of: <T>(value: T): Content<T> => Loaded(value),
  ofNullable: <T>(value: T | null): Content<T> =>
    value == null ? Empty() : Loaded(value),
  map:
    <T, U>(mapper: (x: T) => U) =>
    (content: Content<T>): Content<U> => {
      switch (content[0]) {
        case 'empty':
          return Empty();
        case 'loading':
          return Loading();
        case 'loaded':
          return Loaded(mapper(content[1]));
        default:
          return unreachable(content);
      }
    },
  flatten: <T>(content: Content<Content<T>>): Content<T> => {
    switch (content[0]) {
      case 'empty':
        return Empty();
      case 'loading':
        return Loading();
      case 'loaded':
        switch (content[1][0]) {
          case 'empty':
            return Empty();
          case 'loading':
            return Loading();
          case 'loaded':
            return Loaded(content[1][1]);
          default:
            return unreachable(content[1]);
        }
      default:
        return unreachable(content[0]);
    }
  },
  orElse:
    <T>(otherwise: T) =>
    (content: Content<T>): T =>
      content?.[0] === 'loaded' ? content[1] : otherwise,
};

export type EditableContent<T> = Content<T> | Updating<T> | Deleting<T>;

export type RefreshableContent<T> = Content<T> | Refreshing<T>;

export type RefreshableEditableContent<T> = EditableContent<T> | Refreshing<T>;

export type CreatableContent<T> = Content<T> | Creating<T>;

export type CreatableEditableContent<T> =
  | CreatableContent<T>
  | EditableContent<T>;

export type CreatableEditableRefreshableContent<T> =
  | CreatableContent<T>
  | EditableContent<T>
  | RefreshableContent<T>;

export const RefreshableContent = {
  map:
    <T, U>(mapper: (x: T) => U) =>
    (content: RefreshableContent<T>): RefreshableContent<U> => {
      switch (content[0]) {
        case 'refreshing':
          return Refreshing(Loaded(mapper(content[1])));
        default:
          return Content.map(mapper)(content);
      }
    },
  hasData: <T>([status]: RefreshableContent<T>): boolean =>
    status === 'loaded' || status === 'refreshing',
  toMaybe: <T>(content: RefreshableContent<T>): Maybe<T> => {
    switch (content[0]) {
      case 'refreshing':
      case 'loaded':
        return Just(content[1]);
      default:
        return Nothing();
    }
  },
};

export interface Store<T> {
  byId: Record<number, EditableContent<T>>;
  ids: Content<number[]>;
}
